import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  untracked,
  viewChild
} from '@angular/core';
import '@geodan/gm-map';
import {addLayer, removeLayer} from '@geodan/gm-map-functions/build/dist/ol-map-functions';
import {ResourcesService} from '../../services/resources.service';
import {Feature, Map as OlMap, MapBrowserEvent, View} from 'ol';
import VectorLayer from 'ol/layer/Vector'
import {firstValueFrom, fromEvent, of} from 'rxjs';
import VectorSource from 'ol/source/Vector';
import {FeatureLike} from 'ol/Feature';
import {GameStore} from '../../store/game.store';
import BaseLayer from 'ol/layer/Base';
import {GameState} from '../../models/game-models.model';
import {isQuestionCompleted} from '../../store/game-state.model';
import {Answer} from '../../models/answer.model';
import {DoubleClickZoom, DragPan, DragZoom, KeyboardPan, KeyboardZoom, MouseWheelZoom, PinchZoom} from 'ol/interaction';
import {Pixel} from 'ol/pixel';
import {Geometry} from 'ol/geom';
import {FeatureCollection} from 'geojson';
import {GeoJSON} from 'ol/format';
import {borderLayerStyle, correctStyle, defaultStyle, hoverStyle, wrongStyle} from "./map.ol.styles";

@Component({
  selector: 'gis-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MapComponent {
  protected readonly resourcesService = inject(ResourcesService);
  protected readonly store = inject(GameStore);
  private readonly elRef: ElementRef = inject(ElementRef);

  private readonly ngZone = inject(NgZone);
  readonly gameState = input<GameState>('starting');

  readonly mapConfigError = output<string>();
  readonly info = viewChild<ElementRef<HTMLElement>>('info');

  map?: OlMap;
  questionLayer?: VectorLayer<VectorSource<Feature<Geometry>>>;
  questionBordersLayer?: VectorLayer<VectorSource<Feature<Geometry>>>;
  answerLayerId: number | null = null;

  hoveredFeature: Feature | undefined;
  hoverInfo?: HTMLElement;
  questionFeatures: Feature[] = [];

  constructor() {
    effect(() => this.onQuestionChange());
    effect(() => this.onQuestionStateChange());
    effect(() => this.onGuessesChange());
    this.registerMapCreatedEvent();
  }

  private registerMapCreatedEvent() {
    firstValueFrom(fromEvent(this.elRef.nativeElement, 'gm-map-created')).then((e => this.onMapCreated(e as Event & { detail: { map: OlMap } })));
  }

  onMapCreated(event: Event & { detail: { map: OlMap } }) {
    this.map = event.detail.map;
    this.map.on('click', this.onMapClick);
    this.setQuestionLayer();
    this.setMapView();
    this.setHover();
  }

  onQuestionChange() {
    const question = this.store.currentQuestion();

    untracked(() => {
      // First reset the features, for which the guesses are needed
      this.resetFeatures();
      // Then reset guesses
      this.store.setGuesses([]);
      this.removeAnswerLayer();
      this.setQuestionLayer();
      this.setAnswer();

      if (question) {
        this.setAnswerOptions();
      }
    });
  }

  onQuestionStateChange() {
    const questionState = this.store.questionState();
    const showAnswerOnMap = this.store.showAnswerOnMap();

    untracked(() => {
      if (showAnswerOnMap) {
        this.addAnswerLayer();
      } else {
        this.removeAnswerLayer();
      }

      const question = this.store.currentQuestion();
      if (questionState.feedback === 'wrong-and-over' && question) {
        // apply the correctStyle to the correct answer feature
        const {featureIdKey, featureIdCorrectValue, featureNameKey} = question.gameSetting;
        const correctFeature = this.questionLayer?.getSource()?.getFeatures().find(feature => feature
          .get(featureIdKey) === featureIdCorrectValue);
        if (correctFeature) {
          correctFeature.setStyle(correctStyle);
          if (!correctFeature.get('isAddedToGuesses')) {
            correctFeature.set('isAddedToGuesses', true);
            this.store.onGuess({
              label: correctFeature.get(featureNameKey),
              feature: correctFeature,
              isCorrect: true
            });
          }
        }
      }
    });
  }

  onGuessesChange() {
    this.store.guesses().forEach(guess => {
      guess.feature?.setStyle(guess.isCorrect ? correctStyle : wrongStyle);
    });
  }

  setMapView() {
    // bounding box of the land area of the Netherlands
    const xMin = 12000;
    const yMin = 296000;
    const xMax = 279000;
    const yMax = 630000;
    const view = new View({
      center: [(xMin + xMax) / 2, (yMin + yMax) / 2],
      extent: [xMin, yMin, xMax, yMax],
      projection: 'EPSG:28992',
      zoom: 1,
      minZoom: 1,
      maxZoom: 3,
      showFullExtent: true
    });
    this.map?.setView(view);
    this.map?.getInteractions().clear();
    [new DragPan(), new KeyboardPan(), new PinchZoom(), new MouseWheelZoom(), new KeyboardZoom(), new DoubleClickZoom(), new DragZoom()]
      .forEach(interaction => {
        this.map?.addInteraction(interaction);
      });
  }

  setQuestionLayer() {
    const layerId = this.store.currentQuestion()?.gameSetting.gameLayer ?? this.store.startGameLayerId();

    if (this.questionLayer?.get('id') && this.questionLayer.get('id') !== layerId) {
      this.map?.removeLayer(this.questionLayer);
      if (this.questionBordersLayer) {
        this.map?.removeLayer(this.questionBordersLayer);
        this.questionBordersLayer = undefined;
      }
    }

    if (this.map && !this.map?.getLayers().getArray().find(layer => layer.get('id') === layerId)) {
      // If a vector layer is added to the map and then layer.getSource().getFeatures() is used to create the answers dropdown,
      // the features are returned in random order. Because in the dropdown they need to be in the original order of the WFS features
      // first the features are requested, then stored in this.questionFeatures, and then added to the map.
      this.getQuestionLayerFeatures(layerId as number).subscribe({
        next: (featureCollection) => {
          this.questionFeatures = new GeoJSON().readFeatures(featureCollection, {dataProjection: 'EPSG:28992'});
          this.questionLayer = this.createQuestionVectorLayer();
          this.questionLayer.set('id', layerId);
          this.map?.addLayer(this.questionLayer);
          this.setAnswerOptions();
          this.restorePersistedGuesses();
        },
        error: () => {
          this.store.setError('mapConfigError');
        }
      });
    }
  }

  getQuestionLayerFeatures(layerId: number) {
    const configLayer = this.resourcesService.mapConfig.value()?.map.layers.find(layer => layer.id === layerId);
    if (configLayer) {
      return this.resourcesService.getFeatureCollection(configLayer.source);
    } else {
      return of({type: 'FeatureCollection', features: []} as FeatureCollection);
    }
  }

  createQuestionVectorLayer() {
    return new VectorLayer({
      source: new VectorSource({features: this.questionFeatures}),
      style: defaultStyle
    });
  }

  addQuestionBordersLayer() {
    const features = this.questionLayer?.getSource()?.getFeatures().map(f => f.clone()) ?? [];
    features.forEach(feature => {
      feature.setStyle(borderLayerStyle);
      feature.set('isQuestionBorder', true);
    })
    const options = {
      source: new VectorSource({
        features
      }),
      zIndex: Infinity,
    }
    this.questionBordersLayer = new VectorLayer(options);
    this.map?.addLayer(this.questionBordersLayer);
  }

  resetFeatures() {
    this.store.guesses().forEach(({feature}) => {
      feature.setStyle(defaultStyle);
      feature.unset('isAddedToGuesses', true);
    });
  }

  readonly onMapClick = (event: MapBrowserEvent<PointerEvent | KeyboardEvent | WheelEvent>) => {
    const question = this.store.currentQuestion();
    if (this.map && this.gameState() === 'playing' && !isQuestionCompleted(this.store.questionState())) {
      let feature: Feature<Geometry>;
      const setFeature = (clickedFeature: FeatureLike) => feature = clickedFeature as Feature<Geometry>;
      const options = {
        layerFilter: (layer: BaseLayer) => layer === this.questionLayer
      };
      this.map.forEachFeatureAtPixel(event.pixel, setFeature, options);
      this.ngZone.run(() => {
        if (feature && question) {
          const featureNameKey = question.gameSetting.featureNameKey;
          const isCorrect = feature.get(question.gameSetting.featureIdKey) === question.gameSetting.featureIdCorrectValue;
          this.store.onGuess({label: feature.get(featureNameKey), feature, isCorrect});
        }
      });
    }
  };

  setHover() {
    this.hoverInfo = this.info()?.nativeElement;
    // @ts-expect-error type error
    this.map?.on('pointermove', this.onPointerMove);
    this.map?.getTargetElement().addEventListener('pointerleave', this.onPointerLeave);
  }

  readonly onPointerMove = (event: MapBrowserEvent<PointerEvent>) => {
    if (this.hoveredFeature) {
      this.hoveredFeature?.setStyle(this.getFeatureStyle(this.hoveredFeature, false));
      this.hoveredFeature = undefined;
    }

    if (event.dragging || event.originalEvent.pointerType !== 'mouse') {
      this.hoverInfo!.style.visibility = 'hidden';
      return;
    }

    if (this.gameState() === 'playing' && !this.store.showAnswerOnMap()) {
      const pixel = this.map!.getEventPixel(event.originalEvent);
      const feature = this.map?.forEachFeatureAtPixel(pixel, feature => feature as Feature);
      this.styleFeatureAndShowInfo(feature, pixel, this.hoverInfo!);
      this.hoveredFeature = feature;
    }
  };

  readonly onPointerLeave = () => {
    this.hoveredFeature = undefined;
    if (this.hoverInfo) {
      this.hoverInfo.style.visibility = 'hidden';
    }
  };

  styleFeatureAndShowInfo(feature: Feature | undefined, pixel: Pixel, info: HTMLElement) {
    if (feature && !feature.get('isQuestionBorder')) {
      feature.setStyle(this.getFeatureStyle(feature, true));
      info.style.left = pixel[0] + 'px';
      info.style.top = pixel[1] + 'px';
      if (feature !== this.hoveredFeature) {
        info.style.visibility = 'visible';
        info.innerText = feature.get(this.store.currentQuestion()?.gameSetting.featureNameKey ?? '');
      }
    } else {
      info.style.visibility = 'hidden';
    }
  }

  getFeatureStyle(feature: Feature, isHovered: boolean) {
    const isGuessed = this.store.guesses().find(guess => guess.feature === feature);
    if (isGuessed) {
      return isGuessed.isCorrect ? correctStyle : wrongStyle
    } else if (isQuestionCompleted(this.store.questionState())) {
      return defaultStyle;
    }
    return isHovered ? hoverStyle : defaultStyle;
  }

  addAnswerLayer() {
    const question = this.store.currentQuestion();
    const layerId = question?.answerLayerId ?? null;
    const answerLayer = addLayer(this.map, layerId);
    if (answerLayer) {
      this.answerLayerId = layerId;
      answerLayer.setVisible(true);
      this.store.setAnswerLegendUrl(answerLayer.get('legendUrl') ?? '');

      setTimeout(() => {
        this.store.guesses().forEach(guess => {
          guess.feature.setStyle(defaultStyle);
        });
      });
    }

    if (!this.questionBordersLayer) {
      this.addQuestionBordersLayer();
    }
  }

  removeAnswerLayer() {
    if (this.answerLayerId) {
      removeLayer(this.map, this.answerLayerId);
      this.answerLayerId = null;
      this.store.setAnswerLegendUrl('');

      this.store.guesses().forEach(guess => {
        guess.feature.setStyle(guess.isCorrect ? correctStyle : wrongStyle);
      });
    }

    if (this.questionBordersLayer) {
      this.map?.removeLayer(this.questionBordersLayer);
      this.questionBordersLayer = undefined;
    }
  }

  setAnswerOptions() {
    const question = this.store.currentQuestion();
    if (question) {
      const {
        featureIdKey,
        featureIdCorrectValue,
        featureNameKey
      } = question.gameSetting;

      if (this.questionFeatures?.length && featureIdKey && (featureIdCorrectValue || featureIdCorrectValue === 0) && featureNameKey) {
        const answerOptions = this.questionFeatures.map(feature => {
          const label = feature.get(featureNameKey);
          feature.set('label', label);
          return {
            label,
            isCorrect: feature.get(featureIdKey) === featureIdCorrectValue,
            feature
          };
        });

        this.store.setAnswerOptions(answerOptions);
      }
    }
  }

  /**
   * Rehydrates the guesses persisted for the current question after a browser
   * refresh. Runs once the WFS features have loaded: it matches each
   * {@link PersistedGuess} to its feature by the stable string id
   * (`String(feature.get(featureIdKey))`), re-applies the correct/wrong style,
   * and restores the guesses in the store. Skipped when guesses are already
   * present or nothing was persisted.
   */
  restorePersistedGuesses() {
    const question = this.store.currentQuestion();
    if (!question || this.store.guesses().length) {
      return;
    }

    const persistedGuesses = this.store.persistedGuesses();
    if (!persistedGuesses.length) {
      return;
    }

    const {featureIdKey} = question.gameSetting;
    const restored: Answer[] = [];

    persistedGuesses.forEach(persistedGuess => {
      const feature = this.questionFeatures.find(f => String(f.get(featureIdKey)) === persistedGuess.featureId);
      if (feature) {
        feature.set('isAddedToGuesses', true);
        feature.setStyle(persistedGuess.isCorrect ? correctStyle : wrongStyle);
        restored.push({label: persistedGuess.label, feature, isCorrect: persistedGuess.isCorrect});
      }
    });

    if (restored.length) {
      this.store.restoreGuesses(restored);
    }
  }

  setAnswer() {
    const currentQuestionGameSetting = this.store.currentQuestion()?.gameSetting;
    const key = currentQuestionGameSetting?.featureIdKey as string;
    const value = currentQuestionGameSetting?.featureIdCorrectValue;
    const nameKey = currentQuestionGameSetting?.featureNameKey as string;
    const feature = this.questionLayer?.getSource()?.getFeatures().find(feature => feature.get(key) === value);
    this.store.setAnswer(feature?.get(nameKey) ?? '');
  }

  protected readonly JSON = JSON;
}
